import { FC, useContext, useEffect, useMemo, useRef } from 'react';
import {
  Button,
  ComboBox,
  Dropdown,
  InlineLoading,
  TextInput,
} from '@carbon/react';

import { FreeTextComboBox } from 'components/free-text-combobox';
import { SubtractAlt, WarningFilled } from '@carbon/react/icons';
import { useAtom, useAtomValue } from 'jotai';
import { useStore } from '@tanstack/react-store';
import { NumericFormat } from 'react-number-format';
import { debounce } from 'lodash-es';

import { useWizardStep } from 'components/full-page-wizard';
import { formatNumber } from 'utils/number';

import { AfeWizardCtx } from '../context';
import {
  afeEnumsLoadableAtom,
  createCategoryTypeaheadAtoms,
  createSubCategoryTypeaheadAtoms,
} from '../atoms';
import type {
  AfeFormInstance,
  CostCategoryEnum,
  CostItem,
  EnumOption,
  TypeaheadResult,
} from '../types';
import styles from './cost-information-step.module.scss';

// ─────────────────────────────────────
// CostRow sub-component
// ─────────────────────────────────────

interface CostRowProps {
  form: AfeFormInstance;
  index: number;
  onDelete: () => void;
  showDelete: boolean;
  costCategories: CostCategoryEnum[];
}

/**
 * Single cost line item row with Name, Category (enum seeded + server typeahead,
 * free text), Sub Category (enum seeded + server typeahead scoped to category,
 * free text), Cost, and Delete.
 * Each row creates its own typeahead atom instances for independent search state.
 * @param {CostRowProps} props - Form instance, row index, delete handler, delete visibility, enum categories
 * @return {JSX.Element} A single cost row
 */
const CostRow: FC<CostRowProps> = ({
  form,
  index,
  onDelete,
  showDelete,
  costCategories,
}) => {
  const [, catAtomLoadable] = useMemo(() => createCategoryTypeaheadAtoms(), []);
  const [, subCatAtomLoadable] = useMemo(
    () => createSubCategoryTypeaheadAtoms(),
    []
  );

  const [catState, searchCategory] = useAtom(catAtomLoadable);
  const [subCatState, searchSubCategory] = useAtom(subCatAtomLoadable);

  const debouncedSearchCategory = useMemo(
    () => debounce(searchCategory, 400),
    [searchCategory]
  );
  const debouncedSearchSubCategory = useMemo(
    () => debounce(searchSubCategory, 400),
    [searchSubCategory]
  );

  useEffect(() => {
    return () => {
      debouncedSearchCategory.cancel();
      debouncedSearchSubCategory.cancel();
    };
  }, [debouncedSearchCategory, debouncedSearchSubCategory]);

  const categoryLoading = catState.state === 'loading';
  const categoryError = catState.state === 'hasError';
  const subCatLoading = subCatState.state === 'loading';
  const subCatError = subCatState.state === 'hasError';

  const category = useStore(
    form.store,
    (s) => s.values.costItems[index]?.category ?? ''
  );
  const subCategory = useStore(
    form.store,
    (s) => s.values.costItems[index]?.subCategory ?? ''
  );

  // Memoize enum-derived items to prevent unnecessary ComboBox re-renders
  const enumCategoryItems: TypeaheadResult[] = useMemo(
    () => costCategories.map((c, i) => ({ id: i + 1, value: c.label })),
    [costCategories]
  );

  const enumSubCategoryItems: TypeaheadResult[] = useMemo(
    () =>
      (
        costCategories.find((c) => c.label === category)?.subCategories ?? []
      ).map((s, i) => ({ id: i + 1, value: s.label })),
    [costCategories, category]
  );

  // Show server results when available, otherwise show enum options.
  // Always include a synthetic item for the current form value so the
  // ComboBox can display the selection on remount (same pattern as UWI).
  const categoryItems: TypeaheadResult[] = useMemo(() => {
    const serverResults =
      catState.state === 'hasData' && catState.data
        ? catState.data.results
        : [];
    const base = serverResults.length > 0 ? serverResults : enumCategoryItems;
    if (category && !base.some((i) => i.value === category)) {
      return [{ id: 0, value: category }, ...base];
    }
    return base;
  }, [catState, enumCategoryItems, category]);

  const selectedCategory = category
    ? (categoryItems.find((i) => i.value === category) ?? null)
    : null;

  const subCategoryItems: TypeaheadResult[] = useMemo(() => {
    const serverResults =
      subCatState.state === 'hasData' && subCatState.data
        ? subCatState.data.results
        : [];
    const base =
      serverResults.length > 0 ? serverResults : enumSubCategoryItems;
    if (subCategory && !base.some((i) => i.value === subCategory)) {
      return [{ id: 0, value: subCategory }, ...base];
    }
    return base;
  }, [subCatState, enumSubCategoryItems, subCategory]);

  const selectedSubCategory = subCategory
    ? (subCategoryItems.find((i) => i.value === subCategory) ?? null)
    : null;

  // Refs to track typed text for free-text acceptance
  const catInputRef = useRef(category);

  return (
    <div className={styles.costRow}>
      <form.Field name={`costItems[${index}].name`}>
        {(field) => (
          <TextInput
            id={`cost-name-${index}`}
            labelText="Name of Cost"
            value={field.state.value ?? ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              field.handleChange(e.target.value);
            }}
            className={styles.field}
          />
        )}
      </form.Field>
      <div className={styles.typeaheadWrapper}>
        <ComboBox
          id={`cost-category-${index}`}
          titleText="Category"
          items={categoryItems}
          selectedItem={selectedCategory}
          itemToString={(item: TypeaheadResult | null) => item?.value ?? ''}
          onInputChange={(text: string) => {
            catInputRef.current = text;
            if (text && !categoryItems.some((i) => i.value === text)) {
              debouncedSearchCategory(text);
            }
          }}
          onChange={({ selectedItem }: any) => {
            debouncedSearchCategory.cancel();
            const value = selectedItem?.value ?? catInputRef.current ?? '';
            if (value !== category) {
              form.setFieldValue(`costItems[${index}].category`, value);
              // Reset sub-category when category changes
              form.setFieldValue(`costItems[${index}].subCategory`, '');
            }
          }}
          shouldFilterItem={() => true}
          placeholder="Search category..."
          className={styles.field}
        />
        {categoryLoading && (
          <InlineLoading description="" className={styles.spinner} />
        )}
        {categoryError && <WarningFilled className={styles.errorIcon} />}
      </div>
      <div className={styles.typeaheadWrapper}>
        <FreeTextComboBox
          id={`cost-subcategory-${index}`}
          key={`subcat-${index}-${category}`}
          titleText="Sub Category"
          items={subCategoryItems}
          selectedItem={selectedSubCategory}
          itemToString={(item) => (item as TypeaheadResult | null)?.value ?? ''}
          onInputChange={(text) => {
            if (
              text &&
              !subCategoryItems.some((i) => i.value === text) &&
              category
            ) {
              debouncedSearchSubCategory(text, category);
            }
          }}
          onChange={({ selectedItem }) => {
            debouncedSearchSubCategory.cancel();
            const value = (selectedItem as TypeaheadResult | null)?.value ?? '';
            if (value !== subCategory) {
              form.setFieldValue(`costItems[${index}].subCategory`, value);
            }
          }}
          disabled={!category}
          placeholder="Search sub category..."
          className={styles.field}
        />
        {subCatLoading && (
          <InlineLoading description="" className={styles.spinner} />
        )}
        {subCatError && <WarningFilled className={styles.errorIcon} />}
      </div>
      <form.Field name={`costItems[${index}].cost`}>
        {(field) => (
          <NumericFormat
            type="text"
            customInput={TextInput}
            id={`cost-amount-${index}`}
            labelText="Cost"
            thousandSeparator
            allowNegative={false}
            prefix="$ "
            value={field.state.value === null ? '' : field.state.value}
            onValueChange={(values) => {
              field.handleChange(
                values.value !== '' ? Number(values.value) : null
              );
            }}
            className={styles.field}
          />
        )}
      </form.Field>
      {showDelete && (
        <Button
          kind="ghost"
          size="sm"
          hasIconOnly
          renderIcon={SubtractAlt}
          iconDescription="Delete cost item"
          onClick={onDelete}
          className={styles.deleteButton}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────
// CostInformationStep
// ─────────────────────────────────────

/**
 * Step 4 — Cost Information. Renders repeatable cost line item rows with
 * category/subcategory server typeahead, auto-calculated total, Hold Flag
 * dropdown, and validation bridge for the wizard's Next button.
 * @return {JSX.Element} The cost information step content
 */
const CostInformationStep: FC = () => {
  const { form } = useContext(AfeWizardCtx)!;
  const { setNextEnabled } = useWizardStep();
  const enumsLoadable = useAtomValue(afeEnumsLoadableAtom);

  const heldFlagOptions: EnumOption[] =
    enumsLoadable.state === 'hasData' ? enumsLoadable.data.heldFlag : [];
  const costCategories: CostCategoryEnum[] =
    enumsLoadable.state === 'hasData' ? enumsLoadable.data.costCategories : [];

  const costItems = useStore(form.store, (s) => s.values.costItems);
  const heldFlag = useStore(form.store, (s) => s.values.heldFlag);

  const total = costItems.reduce((sum, item) => sum + (item.cost ?? 0), 0);

  const selectedHeldFlag =
    heldFlagOptions.find((opt) => opt.value === heldFlag) ?? null;

  // Validation bridge
  useEffect(() => {
    const valid =
      costItems.length > 0 &&
      costItems.every(
        (item) => item.name && item.category && item.cost !== null
      );
    setNextEnabled(valid);
  }, [costItems, setNextEnabled]);

  return (
    <div>
      <h2 className={styles.heading}>Cost Information*</h2>
      <p className={styles.subtitle}>Add cost line items for this AFE</p>

      <form.Field name="costItems" mode="array">
        {(costItemsField) => (
          <>
            <div className={styles.costRows}>
              {costItemsField.state.value.map(
                (item: CostItem, index: number) => (
                  <CostRow
                    key={item.id ?? index}
                    form={form}
                    index={index}
                    onDelete={() => costItemsField.removeValue(index)}
                    showDelete={costItemsField.state.value.length > 1}
                    costCategories={costCategories}
                  />
                )
              )}
            </div>

            <Button
              kind="tertiary"
              size="sm"
              onClick={() =>
                costItemsField.pushValue({
                  id: null,
                  name: '',
                  category: '',
                  subCategory: '',
                  cost: null,
                })
              }
              className={styles.addButton}
            >
              Add Cost
            </Button>
          </>
        )}
      </form.Field>

      <div className={styles.totalRow}>
        <div>
          <div className={styles.totalLabel}>Total AFE Costs</div>
          <div className={styles.totalValue}>
            {formatNumber(total, 2, 2, 'currency')}
          </div>
        </div>

        <form.Field name="heldFlag">
          {(field) => (
            <Dropdown
              id="held-flag"
              titleText="Hold Flag"
              label="Select..."
              items={heldFlagOptions}
              itemToString={(item: EnumOption | null) => item?.label ?? ''}
              selectedItem={selectedHeldFlag}
              onChange={({ selectedItem }: any) => {
                field.handleChange(selectedItem?.value ?? null);
              }}
              className={styles.holdFlagDropdown}
            />
          )}
        </form.Field>
      </div>
    </div>
  );
};

export { CostInformationStep };
